import { useEffect, useState, useCallback } from "react";
import {
  Wifi, WifiOff, RefreshCw, ChevronDown, ChevronUp,
  Wrench, Users, Radio, Shield, CheckCircle, AlertTriangle,
  Clock, ArrowUpDown, Database, Zap, ExternalLink, Activity,
} from "lucide-react";
import { exportPDF, exportCSV, todayStr } from "@/utils/exportUtils";

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  wrench: Wrench, users: Users, radio: Radio, shield: Shield,
};
const CATEGORY_COLOR = {
  Maintenance:   { bg: "bg-amber-500/10",  border: "border-amber-500/20",  text: "text-amber-400",  dot: "bg-amber-400" },
  "Crew Management": { bg: "bg-blue-500/10", border: "border-blue-500/20",  text: "text-blue-400",  dot: "bg-blue-400" },
  "Flight Operations": { bg: "bg-teal-500/10", border: "border-teal-500/20", text: "text-teal-400", dot: "bg-teal-400" },
  Regulatory:    { bg: "bg-purple-500/10", border: "border-purple-500/20", text: "text-purple-400", dot: "bg-purple-400" },
};

function StatusBadge({ status }) {
  return status === "live"
    ? <span className="flex items-center gap-1 text-[10px] font-bold text-green-400"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />LIVE</span>
    : <span className="flex items-center gap-1 text-[10px] font-bold text-red-400"><span className="w-1.5 h-1.5 rounded-full bg-red-400" />OFFLINE</span>;
}

function MetricChip({ label, value, color }) {
  return (
    <div className="flex flex-col items-center bg-background/60 rounded-sm px-3 py-2 min-w-[80px]">
      <div className={`text-lg font-mono font-black ${color}`}>{value}</div>
      <div className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold">{label}</div>
    </div>
  );
}

function DataPreviewPanel({ id, data, loading }) {
  if (loading) return <div className="text-xs text-muted-foreground animate-pulse py-4 text-center">Fetching data...</div>;
  if (!data) return null;

  if (id === "amos_ramco") {
    return (
      <div className="space-y-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Work Orders</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-border/50">
              {["WO #","Tail","Component","Type","Due","Status"].map(h => (
                <th key={h} className="px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(data.data?.work_orders || []).slice(0,6).map(w => (
                <tr key={w.wo_number} className="border-b border-border/30 hover:bg-secondary/10">
                  <td className="px-2 py-1.5 font-mono text-blue-400 text-[11px]">{w.wo_number}</td>
                  <td className="px-2 py-1.5 font-mono text-foreground">{w.tail_number}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{w.component}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{w.task_type}</td>
                  <td className="px-2 py-1.5 font-mono text-muted-foreground">{w.due_date}</td>
                  <td className="px-2 py-1.5">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${w.status === "in_progress" ? "bg-amber-500/15 text-amber-400" : w.status === "scheduled" ? "bg-blue-500/15 text-blue-400" : "bg-green-500/15 text-green-400"}`}>{w.status.toUpperCase()}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-2">Airworthiness Directives</div>
        {(data.data?.airworthiness_directives || []).map(ad => (
          <div key={ad.ad_number} className={`flex items-start justify-between p-2 rounded-sm border ${ad.status === "overdue" ? "bg-red-500/5 border-red-500/20" : "bg-card border-border/40"}`}>
            <div>
              <div className="text-xs font-bold text-foreground">{ad.ad_number}</div>
              <div className="text-[11px] text-muted-foreground">{ad.title}</div>
            </div>
            <div className="text-right shrink-0 ml-4">
              <div className="text-[11px] font-mono text-muted-foreground">{ad.compliance_date}</div>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${ad.status === "overdue" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>{ad.status.toUpperCase()}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (id === "jeppesen_aims") {
    return (
      <div className="space-y-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Imported Pairings</div>
        <div className="grid grid-cols-2 gap-2">
          {(data.data?.pairings || []).slice(0,8).map(p => (
            <div key={p.pairing_ref} className="bg-card border border-border/40 rounded-sm p-2">
              <div className="font-mono text-blue-400 text-xs font-bold">{p.pairing_ref}</div>
              <div className="text-[11px] text-muted-foreground">{p.sectors} sectors · {p.block_hours}h · {p.crew_complement} crew</div>
              <span className="text-[9px] font-bold bg-green-500/15 text-green-400 px-1.5 py-0.5 rounded-sm">{p.status.toUpperCase()}</span>
            </div>
          ))}
        </div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-2">Recent Roster Entries ({data.data?.roster_entries?.length})</div>
        <div className="text-xs text-muted-foreground">Latest sync: {data.fetched_at}</div>
      </div>
    );
  }

  if (id === "acars_fdp") {
    return (
      <div className="space-y-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Live FDP Feed</div>
        {(data.data?.fdp_feeds || []).map(f => (
          <div key={f.flight_number} className={`flex items-center justify-between p-2 rounded-sm border ${f.alert ? "bg-red-500/5 border-red-500/20" : "bg-card border-border/40"}`}>
            <div className="flex items-center gap-3">
              {f.alert && <AlertTriangle size={11} className="text-red-400 shrink-0" />}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-foreground text-xs">{f.flight_number}</span>
                  <span className="text-[10px] text-muted-foreground font-mono">{f.tail_number}</span>
                  <span className="text-[10px] text-muted-foreground">{f.origin}→{f.destination}</span>
                </div>
                <div className="text-[10px] text-muted-foreground">FDP: {f.fdp_actual_hours}h actual vs {f.fdp_planned_hours}h planned</div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className={`text-xs font-mono font-bold ${Math.abs(f.deviation_minutes) > 30 ? "text-amber-400" : "text-green-400"}`}>{f.deviation_minutes > 0 ? "+" : ""}{f.deviation_minutes}m</div>
              {f.wocl_exposure && <div className="text-[9px] font-bold text-purple-400">WOCL</div>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (id === "dgca_sdms") {
    return (
      <div className="space-y-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Submission Log</div>
        {(data.data?.submissions || []).map(s => (
          <div key={s.ref_number} className="flex items-center justify-between p-2 rounded-sm border border-border/40 bg-card">
            <div>
              <div className="font-mono text-blue-400 text-[11px] font-bold">{s.ref_number}</div>
              <div className="text-xs text-foreground">{s.report_type}</div>
              <div className="text-[10px] text-muted-foreground">{s.dgca_ref} · {s.submission_date}</div>
            </div>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm border ${
              s.status === "closed" ? "bg-green-500/10 text-green-400 border-green-500/20" :
              s.status === "acknowledged" ? "bg-blue-500/10 text-blue-400 border-blue-500/20" :
              s.status === "under_review" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" :
              "bg-primary/10 text-primary border-primary/20"
            }`}>{s.status.replace("_"," ").toUpperCase()}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

function IntegrationCard({ intg, onSync }) {
  const [expanded, setExpanded] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const Icon = ICON_MAP[intg.icon] || Database;
  const cc = CATEGORY_COLOR[intg.category] || CATEGORY_COLOR.Regulatory;

  const loadPreview = async () => {
    if (previewData) { setExpanded(v => !v); return; }
    setExpanded(true);
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem("fs_token");
      const r = await fetch(`${API}/api/integrations/${intg.id}/data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData(await r.json());
    } catch (_) {}
    setPreviewLoading(false);
  };

  const handleSync = async (e) => {
    e.stopPropagation();
    setSyncing(true);
    await onSync(intg.id);
    setSyncing(false);
  };

  return (
    <div className={`bg-card border ${cc.border} rounded-sm overflow-hidden transition-all`} data-testid={`integration-card-${intg.id}`}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`w-9 h-9 rounded-sm ${cc.bg} flex items-center justify-center shrink-0`}>
              <Icon size={16} className={cc.text} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="text-sm font-bold text-foreground">{intg.name}</div>
                <StatusBadge status={intg.status} />
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{intg.vendor}</div>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${cc.bg} ${cc.text} border ${cc.border} mt-1 inline-block`}>{intg.category}</span>
            </div>
          </div>
          <button onClick={handleSync} disabled={syncing}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-border rounded-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors disabled:opacity-50"
            data-testid={`sync-btn-${intg.id}`}>
            <RefreshCw size={10} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing..." : "Sync"}
          </button>
        </div>

        <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{intg.description}</p>

        {/* Metrics */}
        <div className="flex gap-2 mt-3 flex-wrap">
          <MetricChip label="Records" value={intg.records_synced?.toLocaleString()} color="text-primary" />
          <MetricChip label="Uptime" value={`${intg.uptime_pct}%`} color="text-green-400" />
          <MetricChip label="Latency" value={`${intg.latency_ms}ms`} color="text-amber-400" />
          <MetricChip label="Interval" value={`${intg.sync_interval_min}m`} color="text-foreground" />
        </div>

        {/* Meta */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><Clock size={9} /> Last sync: {intg.last_sync}</span>
          <span className="flex items-center gap-1"><ArrowUpDown size={9} /> {intg.data_direction}</span>
          <span className="flex items-center gap-1"><Shield size={9} /> {intg.auth_type}</span>
        </div>

        {/* Capabilities */}
        <div className="mt-3 flex flex-wrap gap-1">
          {(intg.capabilities || []).map(cap => (
            <span key={cap} className="text-[10px] bg-background/60 border border-border/50 text-muted-foreground px-2 py-0.5 rounded-sm">{cap}</span>
          ))}
        </div>

        <button onClick={loadPreview}
          className="mt-3 flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors"
          data-testid={`preview-btn-${intg.id}`}>
          <Database size={10} /> {expanded ? "Hide" : "View"} Data Preview
          {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
        </button>
      </div>

      {/* Data Preview */}
      {expanded && (
        <div className="border-t border-border/40 bg-background/40 p-4">
          <DataPreviewPanel id={intg.id} data={previewData} loading={previewLoading} />
        </div>
      )}
    </div>
  );
}

export default function IntegrationsHub() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncResults, setSyncResults] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("fs_token");
      const r = await fetch(`${API}/api/integrations/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      setIntegrations(d.integrations || []);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async (id) => {
    try {
      const token = localStorage.getItem("fs_token");
      const r = await fetch(`${API}/api/integrations/${id}/sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      setSyncResults(prev => ({ ...prev, [id]: d }));
      setTimeout(() => setSyncResults(prev => { const n = {...prev}; delete n[id]; return n; }), 4000);
    } catch (_) {}
  };

  const handleExportPDF = () => {
    const { exportPDF: pdf } = require("@/utils/exportUtils");
    exportPDF({
      title: "Integration Connectors Report",
      subtitle: `${integrations.length} active integrations · ${todayStr()}`,
      columns: [
        { header: "Name",       key: "name",         width: 50 },
        { header: "Vendor",     key: "vendor",       width: 50 },
        { header: "Category",   key: "category",     width: 30 },
        { header: "Status",     key: "status",       width: 20 },
        { header: "Records",    key: "records_synced", width: 20 },
        { header: "Uptime %",   key: "uptime_pct",   width: 20 },
        { header: "Last Sync",  key: "last_sync",    width: 38 },
        { header: "Direction",  key: "data_direction", width: 25 },
      ],
      rows: integrations.map(i => ({ ...i, status: i.status.toUpperCase() })),
      filename: `integrations-status-${todayStr()}.pdf`,
    });
  };

  if (loading) return (
    <div className="flex-1 p-6 flex items-center justify-center">
      <div className="text-primary font-mono text-sm animate-pulse">CONNECTING TO INTEGRATION HUB...</div>
    </div>
  );

  const live = integrations.filter(i => i.status === "live").length;

  return (
    <div className="p-6 space-y-6" data-testid="integrations-hub">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-foreground tracking-tight">Integrations Hub</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            External system connectors — {live} of {integrations.length} active
          </p>
        </div>
        <button onClick={handleExportPDF}
          className="flex items-center gap-1.5 px-3 py-2 border border-border text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 rounded-sm transition-colors"
          data-testid="integrations-export-btn">
          Export Report
        </button>
      </div>

      {/* Status bar */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Active Connectors", value: live, color: "text-green-400", Icon: CheckCircle },
          { label: "Total Records Synced", value: integrations.reduce((s,i) => s+(i.records_synced||0),0).toLocaleString(), color: "text-primary", Icon: Database },
          { label: "Avg Latency", value: `${Math.round(integrations.reduce((s,i) => s+(i.latency_ms||0),0)/Math.max(integrations.length,1))}ms`, color: "text-amber-400", Icon: Activity },
          { label: "System Uptime", value: `${(integrations.reduce((s,i) => s+(i.uptime_pct||0),0)/Math.max(integrations.length,1)).toFixed(1)}%`, color: "text-green-400", Icon: Wifi },
        ].map(({ label, value, color, Icon }) => (
          <div key={label} className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
              <Icon size={13} className={color} />
            </div>
            <div className={`text-2xl font-mono font-black ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Sync result toasts */}
      {Object.entries(syncResults).map(([id, res]) => (
        <div key={id} className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-sm text-xs text-green-400">
          <CheckCircle size={12} /> {res.message} · {res.records_synced} records · {res.duration_ms}ms
        </div>
      ))}

      {/* Integration Cards */}
      <div className="grid grid-cols-2 gap-4">
        {integrations.map(intg => (
          <IntegrationCard key={intg.id} intg={intg} onSync={handleSync} />
        ))}
      </div>

      <div className="text-[10px] text-muted-foreground text-center pt-2">
        All integration connectors operate via secure authenticated endpoints. Data shown is live mock feed for demonstration purposes.
      </div>
    </div>
  );
}
